import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X, Plus, ArrowUp, ArrowDown, Save, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiGetHero, apiSaveHero, apiUploadImage } from "../adminApi";

const uid = () => Math.random().toString(36).slice(2, 10);

function SlideEditor({ slide, index, total, onChange, onMove, onRemove }) {
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const res = await apiUploadImage(file, "rinova/hero", undefined, "hero");
      onChange({ ...slide, image: res.url });
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploading(false);
    }
  };

  const onPickVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingVideo(true);
    try {
      const res = await apiUploadImage(file, "rinova/hero", undefined, "hero");
      onChange({ ...slide, video: res.url, poster: res.poster || "" });
      toast.success("Video caricato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Caricamento video non riuscito");
    } finally {
      setUploadingVideo(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-line bg-surface2 p-4" data-testid={`hero-slide-${index}`}>
      {/* Anteprima con proporzione hero esatta 16:6 (video prioritario, poi foto) */}
      <div className="relative aspect-[16/6] w-full overflow-hidden rounded-xl bg-card">
        {slide.video ? (
          <video ref={videoRef} src={slide.video} poster={slide.poster || undefined}
            className="absolute inset-0 h-full w-full object-cover" autoPlay muted loop playsInline />
        ) : slide.image ? (
          <img src={slide.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-tertiary">Nessuna foto/video (resta il design predefinito)</div>
        )}
        {(slide.title || slide.cta_label) && (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/90 to-transparent p-5">
            {slide.title && <p className="max-w-lg text-lg font-extrabold text-chalk">{slide.title}</p>}
            {slide.cta_label && <span className="mt-2 inline-flex rounded-full bg-lime px-4 py-1.5 text-xs font-bold text-ink">{slide.cta_label}</span>}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid={`hero-input-${index}`} />
        <Button size="sm" variant="secondary" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`hero-upload-${index}`}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {slide.image ? "Sostituisci foto" : "Carica foto"}
        </Button>
        {slide.image && (
          <Button size="sm" variant="ghost" onClick={() => onChange({ ...slide, image: "" })}>Rimuovi foto</Button>
        )}
        <label>
          <input type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov" className="hidden" onChange={onPickVideo} data-testid={`hero-video-input-${index}`} />
          <Button asChild size="sm" variant="secondary" disabled={uploadingVideo} data-testid={`hero-video-upload-${index}`}>
            <span className="cursor-pointer">
              {uploadingVideo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />}
              {slide.video ? "Sostituisci video" : "Carica video (mp4)"}
            </span>
          </Button>
        </label>
        {slide.video && (
          <Button size="sm" variant="ghost" onClick={() => onChange({ ...slide, video: "", poster: "" })}>Rimuovi video</Button>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" disabled={index === 0} onClick={() => onMove(index, -1)} aria-label="Sposta su"><ArrowUp className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" disabled={index === total - 1} onClick={() => onMove(index, 1)} aria-label="Sposta giù"><ArrowDown className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => onRemove(index)} aria-label="Elimina slide"><X className="h-4 w-4" /></Button>
        </div>
      </div>

      {slide.video && <p className="-mt-1 text-xs text-steel">Il video parte in automatico, senza audio e in loop. Consigliato mp4 orizzontale (16:6).</p>}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label>Titolo</Label>
          <Input value={slide.title || ""} onChange={(e) => onChange({ ...slide, title: e.target.value })} placeholder="Es. Tecnologia ricondizionata, come nuova" />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Testo pulsante</Label>
          <Input value={slide.cta_label || ""} onChange={(e) => onChange({ ...slide, cta_label: e.target.value })} placeholder="Es. Scopri il catalogo" />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Link</Label>
          <Input value={slide.link || ""} onChange={(e) => onChange({ ...slide, link: e.target.value })} placeholder="/catalogo, /offerte…" />
        </div>
      </div>
    </div>
  );
}

export default function HeroManager() {
  const [slides, setSlides] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGetHero().then((d) => setSlides(d.slides || [])).catch(() => setSlides([]));
  }, []);

  const updateSlide = (i, next) => setSlides((s) => s.map((sl, idx) => (idx === i ? next : sl)));
  const moveSlide = (i, dir) => setSlides((s) => {
    const arr = [...s];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return s;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });
  const removeSlide = (i) => setSlides((s) => s.filter((_, idx) => idx !== i));
  const addSlide = () => setSlides((s) => [...s, { id: uid(), image: "", title: "", cta_label: "", link: "/catalogo" }]);

  const save = async () => {
    setSaving(true);
    try {
      await apiSaveHero(slides);
      toast.success("Hero salvato e pubblicato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="hero-manager">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-chalk">Hero</h1>
          <p className="mt-1 max-w-2xl text-sm text-steel">
            Gestisci le immagini della parte alta della home. Le foto vengono ottimizzate automaticamente in proporzione 16:6.
            Se non aggiungi slide, resta il design predefinito.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/?preview=1" target="_blank" rel="noreferrer"><Button variant="secondary" size="sm" data-testid="hero-preview">Anteprima sito</Button></a>
          <Button variant="lime" size="sm" onClick={save} disabled={saving || slides === null} data-testid="hero-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva e pubblica
          </Button>
        </div>
      </div>

      {slides === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-tertiary" /></div>
      ) : (
        <div className="flex flex-col gap-4">
          {slides.length === 0 && (
            <div className="rounded-2xl border border-dashed border-line p-8 text-center text-sm text-steel">
              Nessuna slide personalizzata. Aggiungi la prima slide per mettere le tue foto nell'hero.
            </div>
          )}
          {slides.map((sl, i) => (
            <SlideEditor
              key={sl.id || i}
              slide={sl}
              index={i}
              total={slides.length}
              onChange={(next) => updateSlide(i, next)}
              onMove={moveSlide}
              onRemove={removeSlide}
            />
          ))}
          {slides.length < 6 && (
            <Button variant="secondary" className="self-start" onClick={addSlide} data-testid="hero-add-slide">
              <Plus className="h-4 w-4" /> Aggiungi slide
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
